// NEW_FILE_JUSTIFIED: Full-page email compose component with To/CC/BCC/Subject/Body/Attachments — no existing component handles email composition
import { useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Send, Paperclip, Save } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useComposeEmail } from '@/hooks/useComposeEmail';
import { ComposeAttachmentList } from '@/components/messages/ComposeAttachmentList';
import { ComposeFormFields } from '@/components/messages/ComposeFormFields';
import type { IComposeEmailProps } from '@/@types/gmail';

export function ComposeEmail({ onBack, onSend, onSaveDraft, initialData }: IComposeEmailProps) {
  const form = useComposeEmail(initialData);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-save every 30 seconds. Use form.getComposeData (memoized) not the whole
  // form object, which is a new object literal each render and would tear down the
  // interval on every re-render, making auto-save non-functional.
  useEffect(() => {
    autoSaveTimerRef.current = setInterval(() => {
      const data = form.getComposeData();
      if (data.to.length > 0 || data.subject || data.bodyHtml) {
        onSaveDraft(data).catch(() => {
          // Auto-save is best-effort
        });
      }
    }, 30_000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is a new object each render; only form.getComposeData (memoized) is needed to avoid tearing down the interval
  }, [form.getComposeData, onSaveDraft]);

  const handleSend = useCallback(async () => {
    const data = form.getComposeData();
    if (data.to.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (!data.subject) {
      toast.error('Please add a subject');
      return;
    }
    form.setIsSending(true);
    try {
      await onSend(data);
      toast.success('Email sent successfully');
      form.reset();
      onBack();
    } catch {
      toast.error('Failed to send email. Please try again.');
    } finally {
      form.setIsSending(false);
    }
  }, [form, onSend, onBack]);

  const handleSaveDraft = useCallback(async () => {
    const data = form.getComposeData();
    try {
      await onSaveDraft(data);
      toast.success('Draft saved');
    } catch {
      toast.error('Failed to save draft');
    }
  }, [form, onSaveDraft]);

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        form.addAttachments(e.target.files);
        e.target.value = '';
      }
    },
    [form]
  );

  const handleBodyInput = useCallback(() => {
    if (bodyRef.current) {
      form.setBodyHtml(bodyRef.current.innerHTML);
    }
  }, [form]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files.length > 0) {
        form.addAttachments(e.dataTransfer.files);
      }
    },
    [form]
  );

  return (
    <div
      className="flex flex-1 flex-col overflow-hidden"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to email list">
            <ArrowLeft size={16} aria-hidden="true" />
          </Button>
          <h2 className="text-lg font-semibold text-foreground">New Message</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleSaveDraft}>
            <Save size={14} className="mr-1" aria-hidden="true" />
            Save Draft
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={form.isSending}
            aria-label={form.isSending ? 'Sending email, please wait' : 'Send email'}
          >
            <Send size={14} className="mr-1" aria-hidden="true" />
            {form.isSending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-auto p-4 gap-2">
        <ComposeFormFields
          to={form.to}
          setTo={form.setTo}
          cc={form.cc}
          setCc={form.setCc}
          bcc={form.bcc}
          setBcc={form.setBcc}
          subject={form.subject}
          setSubject={form.setSubject}
          showCcBcc={form.showCcBcc}
          toggleCcBcc={form.toggleCcBcc}
        />

        <Separator className="my-2" />

        <div
          ref={bodyRef}
          contentEditable
          onInput={handleBodyInput}
          className={cn(
            'flex-1 min-h-[200px] rounded-md border border-border p-3 text-sm text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring overflow-auto'
          )}
          role="textbox"
          aria-label="Email body"
          aria-multiline="true"
          dangerouslySetInnerHTML={
            initialData?.bodyHtml ? { __html: initialData.bodyHtml } : undefined
          }
        />

        <ComposeAttachmentList attachments={form.attachments} onRemove={form.removeAttachment} />

        <div className="flex items-center gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={handleFileSelect}>
            <Paperclip size={14} className="mr-1" aria-hidden="true" />
            Attach files
          </Button>
          <span className="text-xs text-muted-foreground">or drag and drop files here</span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}
